import React, { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "@clerk/react";
import { OBSERVABILITY_EVENTS } from "@workspace/observability";
import { track } from "@workspace/observability/web";
import { t } from "@workspace/strings/web";
import {
  ApiError,
  getGetSessionQueryOptions,
  useGetSession,
} from "@workspace/api-client-react";
import type { WorkspaceWithRole } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { ACTIVE_WORKSPACE_STORAGE_KEY } from "@/lib/storage-keys";
import { useSignOut } from "@/hooks/use-sign-out";

// Defined in lib/storage-keys so pure modules and the pre-React boot path in
// main.tsx can use it without importing the workspace context. Re-exported
// here because existing call sites import it from this module.
export { ACTIVE_WORKSPACE_STORAGE_KEY } from "@/lib/storage-keys";
const STORAGE_KEY = ACTIVE_WORKSPACE_STORAGE_KEY;

interface WorkspaceContextType {
  activeWorkspaceId: string | null;
  activeWorkspace: WorkspaceWithRole | null;
  setActiveWorkspaceId: (id: string) => void;
  clearActiveWorkspaceId: () => void;
  workspaces: WorkspaceWithRole[];
  isLoading: boolean;
  refetchSession: () => Promise<unknown>;
}

const WorkspaceContext = createContext<WorkspaceContextType | null>(null);

export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
  const { isLoaded, isSignedIn } = useAuth();
  const signOut = useSignOut();
  const { data: session, isLoading, isError, error, refetch } = useGetSession({
    query: {
      ...getGetSessionQueryOptions(),
      enabled: isLoaded && isSignedIn,
      retry: (failureCount, err) => {
        if (err instanceof ApiError && err.status === 401 && failureCount < 3) {
          return true;
        }
        return failureCount < 1;
      },
    },
  });
  const [activeWorkspaceId, setActiveWorkspaceIdState] = useState<string | null>(() => {
    return localStorage.getItem(STORAGE_KEY);
  });

  const workspaces = session?.workspaces || [];

  // Clear stale workspace id if it no longer exists — do not auto-pick another.
  useEffect(() => {
    if (!isLoading && activeWorkspaceId) {
      const stillValid = workspaces.some((w) => w.id === activeWorkspaceId);
      if (!stillValid) {
        setActiveWorkspaceIdState(null);
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, [isLoading, workspaces, activeWorkspaceId]);

  const setActiveWorkspaceId = (id: string) => {
    setActiveWorkspaceIdState(id);
    localStorage.setItem(STORAGE_KEY, id);
    track(OBSERVABILITY_EVENTS["workspace.selected"], { workspace_id: id });
  };

  const clearActiveWorkspaceId = () => {
    setActiveWorkspaceIdState(null);
    localStorage.removeItem(STORAGE_KEY);
  };

  const activeWorkspace = workspaces.find((w) => w.id === activeWorkspaceId) || null;

  if (!isLoaded || (isSignedIn && isLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Spinner className="size-8" />
      </div>
    );
  }

  if (isError) {
    const is401 =
      error instanceof ApiError && error.status === 401;

    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-6">
        <div className="max-w-md text-center space-y-4">
          <h1 className="text-xl font-semibold">{t("webMisc.workspaceLoad.error")}</h1>
          {is401 ? (
            <>
              <p className="text-sm text-muted-foreground">
                {t("webMisc.workspaceLoad.sessionExpired")}
              </p>
              <Button
                onClick={() => void signOut()}
              >
                {t("webMisc.workspaceLoad.signInAgain")}
              </Button>
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                {error && typeof error === "object" && "message" in error
                  ? String((error as { message: unknown }).message)
                  : t("webMisc.workspaceLoad.failed")}
              </p>
              <Button onClick={() => refetch()}>{t("webMisc.action.tryAgain")}</Button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <WorkspaceContext.Provider
      value={{
        activeWorkspaceId,
        activeWorkspace,
        setActiveWorkspaceId,
        clearActiveWorkspaceId,
        workspaces,
        isLoading,
        refetchSession: () => refetch(),
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error("useWorkspace must be used within a WorkspaceProvider");
  }
  return context;
}

/** Like useWorkspace but returns null outside WorkspaceProvider (e.g. handoff flow). */
export function useWorkspaceOptional() {
  return useContext(WorkspaceContext);
}

/** Keep the active workspace aligned with a device claim / player activation. */
export function syncActiveWorkspaceId(id: string) {
  localStorage.setItem(STORAGE_KEY, id);
}
